import {LibraryLayout} from "@/components/layouts/page/LibraryLayout";
import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import {TableLayout} from "@/components/layouts/page/TableLayout";
import {RootIcon} from "@/components/ui/icons/RootIcon";
import jnjLibrary from "@/pages/common/LibraryPage/jnjLibrary.json";
import mayolyLibrary from "@/pages/common/LibraryPage/mayolyLibrary.json";

import {useMemo, useState} from "react";

import requirements_preductal from "@fallbackVideos/Requirements_for_a_visit_to_Preductal_OD.png";
import requirements_tujeo from "@fallbackVideos/Requirements_for_visiting_Tujeo.png";
import research_choice_2 from "@fallbackVideos/Research_CHOICE_2.png";
import doctorObjections from "@fallbackVideos/doctor-objections.jpg";
import painxicam from "@fallbackVideos/painxicam.png";
import rolexin from "@fallbackVideos/rolexin.png";
import sideEffects from "@fallbackVideos/side-effects.png";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";

import styles from "./styles.module.scss";

const FILE_TYPE_ICONS = {
  pdf: "file-pdf",
  pptx: "file-pptx",
  docx: "file-docx",
  xlsx: "file-xlsx",
};

const getFileTypeIcon = (type) =>
  FILE_TYPE_ICONS[String(type || "").toLowerCase()] || "file-default";

const LIBRARY_TENANTS = {
  mayoly: {
    origin:
      import.meta.env.VITE_MAYOLY_REMOTE_ORIGIN || "https://mayoly.cosmart.app",
    pathPrefix: "mayoly",
    manifest: mayolyLibrary,
  },
  jnj: {
    origin: import.meta.env.VITE_JNJ_REMOTE_ORIGIN || "https://jnj.cosmart.app",
    pathPrefix: "jnj",
    manifest: jnjLibrary,
  },
};

const extractTypeFromNameOrUrl = (name = "", url = "") => {
  const [withoutQuery = ""] = (url || name).split("?");
  const [source = ""] = withoutQuery.split("#");
  const segments = source.split(".");
  const ext = segments.length ? segments[segments.length - 1] : "";
  return ext && ext !== source ? ext.toUpperCase() : "-";
};

const resolveLibraryUrl = (origin, pathPrefix, url = "", name = "") => {
  if (!url && !name) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) return `${origin}${url}`;
  if (url) return `${origin}/video/${pathPrefix}/${encodeURIComponent(url)}`;
  return `${origin}/video/${pathPrefix}/${encodeURIComponent(name)}`;
};

const compareValues = (a, b, direction = "asc") => {
  if (a === b) return 0;

  if (a == null) return direction === "asc" ? -1 : 1;
  if (b == null) return direction === "asc" ? 1 : -1;

  const result =
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });

  return direction === "desc" ? -result : result;
};

export const LibraryPage = () => {
  const {t} = useTranslation();
  const [sort, setSort] = useState({value: "uploaded_at", direction: "desc"});
  const {isJnj, isAbbott, isAkvion} = useSelector(
    (state) => state.common.settings,
  );
  const servier_test_server = window.location.host.includes("servier");
  const sanofi_test_server = window.location.host.includes("sanofi");
  const documentsTenant = window.location.host.includes("mayoly")
    ? "mayoly"
    : isJnj || window.location.host.includes("jnj")
      ? "jnj"
      : null;

  const videos = useMemo(() => {
    if (servier_test_server) {
      return [
        {
          id: uuidv4(),
          title: t("video_servier_1.name"),
          description: t("video_servier_1.desc"),
          category: t("video_servier_1.line"),
          time: t("video_servier_1.time"),
          image: requirements_preductal,
          preview: (
            <img
              src={requirements_preductal}
              alt={"preview"}
            />
          ),
          videoStream: "/video/Requirements_for_a_visit_to_Preductal_OD.mp4",
        },
        {
          id: uuidv4(),
          title: t("video_servier_2.name"),
          description: t("video_servier_2.desc"),
          category: t("video_servier_2.line"),
          time: t("video_servier_2.time"),
          image: research_choice_2,
          preview: (
            <img
              src={research_choice_2}
              alt={"preview"}
            />
          ),
          videoStream: "/video/Research_CHOICE_2.mp4",
        },
      ];
    }

    if (sanofi_test_server) {
      return [
        {
          id: uuidv4(),
          title: t("video_sanofi_1.name"),
          description: t("video_sanofi_1.desc"),
          category: t("video_sanofi_1.line"),
          time: t("video_sanofi_1.time"),
          image: requirements_tujeo,
          preview: (
            <img
              src={requirements_tujeo}
              alt={"preview"}
            />
          ),
          videoStream: "/video/Requirements_for_visiting_Tujeo.mp4",
        },
      ];
    }

    return [
      {
        id: uuidv4(),
        title: t("video_1.name"),
        description: t("video_1.desc"),
        category: t("video_1.line"),
        time: t("video_1.time"),
        image: doctorObjections,
        preview: (
          <img
            src={doctorObjections}
            alt={"preview"}
          />
        ),
        videoStream: "/video/video.mp4",
      },
      {
        id: uuidv4(),
        title: t("video_2.name"),
        description: t("video_2.desc"),
        category: t("video_2.line"),
        time: t("video_2.time"),
        image: rolexin,
        preview: (
          <img
            src={rolexin}
            alt={"preview"}
          />
        ),
        videoStream: "/video/video.mp4",
      },
      {
        id: uuidv4(),
        title: t("video_3.name"),
        description: t("video_3.desc"),
        category: t("video_3.line"),
        time: t("video_3.time"),
        image: painxicam,
        preview: (
          <img
            src={painxicam}
            alt={"preview"}
          />
        ),
        videoStream: "/video/video.mp4",
      },
      {
        id: uuidv4(),
        title: t("video_4.name"),
        description: t("video_4.desc"),
        category: t("video_4.line"),
        time: t("video_4.time"),
        image: sideEffects,
        preview: (
          <img
            src={sideEffects}
            alt={"preview"}
          />
        ),
        videoStream: "/video/video.mp4",
      },
    ];
  }, [servier_test_server, sanofi_test_server, t]);

  const columns = useMemo(
    () => [
      {
        Header: "Тип",
        accessor: "type",
        width: 90,
        Cell: ({value}) => {
          return (
            <div className={"flex items-center gap-[8rem]"}>
              <RootIcon
                id={getFileTypeIcon(value)}
                width={32}
                height={32}
              />
            </div>
          );
        },
      },
      {
        Header: "Имя файла",
        accessor: "name",
        width: 532,
      },
      {
        Header: "Размер",
        accessor: "size",
        width: 147,
      },
      {
        Header: "Дата загрузки",
        accessor: "uploaded_at",
        width: 262,
      },
      {
        Header: "Продукт",
        accessor: "category",
        width: 262,
      },
      {
        Header: "Действие",
        accessor: "action",
        width: 132,
        Cell: ({row}) => {
          const hasUrl = Boolean(row.original.url);

          return (
            <div className={styles.downloadCell}>
              <a
                href={hasUrl ? row.original.url : undefined}
                download
                target={"_blank"}
                rel={"noreferrer"}
                className={styles.downloadButton}
                onClick={(e) => {
                  if (!hasUrl) e.preventDefault();
                }}
              >
                <RootIcon
                  id={"download-file"}
                  width={16}
                  height={16}
                />
                <RootIcon
                  id={"download-file-active"}
                  width={16}
                  height={16}
                />
              </a>
            </div>
          );
        },
      },
    ],
    [],
  );

  const manifestBaseDocuments = useMemo(() => {
    if (!documentsTenant) return [];

    const {origin, pathPrefix, manifest} = LIBRARY_TENANTS[documentsTenant];

    return manifest.map((item) => {
      const resolvedUrl = resolveLibraryUrl(
        origin,
        pathPrefix,
        item.url || "",
        item.name || "",
      );

      return {
        ...item,
        url: resolvedUrl,
        type: extractTypeFromNameOrUrl(item.name, resolvedUrl),
        size: item.size || "",
        size_bytes: item.size_bytes || 0,
        uploaded_at: item.uploaded_at || "",
      };
    });
  }, [documentsTenant]);
  const sortedDocuments = useMemo(() => {
    if (!Array.isArray(manifestBaseDocuments)) return [];

    const field = sort?.value;
    const direction = sort?.direction || "asc";
    if (!field) return manifestBaseDocuments;

    return [...manifestBaseDocuments].sort((left, right) => {
      if (field === "size") {
        return compareValues(left.size_bytes, right.size_bytes, direction);
      }

      if (field === "uploaded_at") {
        return compareValues(
          new Date(left.uploaded_at).getTime(),
          new Date(right.uploaded_at).getTime(),
          direction,
        );
      }

      return compareValues(left[field], right[field], direction);
    });
  }, [manifestBaseDocuments, sort]);

  if (documentsTenant) {
    return (
      <div
        className={"container-big"}
        style={{marginBottom: "40rem"}}
      >
        <ScreenLayout>
          <div className={styles.documentsTable}>
            <TableLayout
              serverStatus={"success"}
              columns={columns}
              data={sortedDocuments}
              dataSort={sort}
              selectSort={setSort}
              pagination={false}
              search={false}
              pageSize={false}
              settings={false}
            />
          </div>
        </ScreenLayout>
      </div>
    );
  }

  return (
    <div className={"container-big"}>
      <div className={styles.library}>
        <LibraryLayout videos={isJnj || isAbbott || isAkvion ? [] : videos} />
      </div>
    </div>
  );
};
